import { LoadingIndicator } from 'components/loading'
import { useHistoricoConsultaPecQuery } from 'graphql/hooks.generated'
import {
  EncaminhamentoExternoEspecializado,
  ReceitaMedicamento,
  RegistroVacinacao,
  SolicitacaoExame,
} from 'graphql/types.generated'
import React, { memo } from 'react'
import { HistoricoPanelProps } from 'view/atendimentos/detail/historico/model/model-historico'
import {
  convertMedicoesToHistoricoMedicaoModel,
  convertToEvolucaoProblema,
  mountInfosAdministrativasAtendimentosRecentes,
} from 'view/atendimentos/detail/historico/utils/utils-historico'
import { tipoParticipacaoAtendimentoRecord } from 'view/atendimentos/detail/soap/finalizacao/model-finalizacao'

import HistoricoSection from '../../../../HistoricoSection'
import HistoricoDesfechoPecPanel from '../../HistoricoDesfechoPecPanel'
import HistoricoGapPanel from '../../HistoricoGapPanel'
import HistoricoNoDetailAvailable from '../../HistoricoNoDetailAvailable'
import HistoricoRegistrosAnterioresPanel from '../../HistoricoRegistrosAnterioresPanel'
import HistoricoAntecedenteSection from './HistoricoAntecedenteSection'
import HistoricoAvaliacaoPecPanel from './HistoricoAvaliacaoPecPanel'
import HistoricoObjetivoPecPanel from './HistoricoObjetivoPecPanel'
import HistoricoPlanoPecPanel from './HistoricoPlanoPecPanel'
import HistoricoSubjetivoPecPanel from './HistoricoSubjetivoPecPanel'

interface HistoricoConsultaPanelProps extends HistoricoPanelProps {}

function HistoricoConsultaPanel(props: HistoricoConsultaPanelProps) {
  const { idAtend, idadeOrigemAtendimento, dataNascimento, isAtendObservacao } = props
  const {
    data: { historicoAtendimentoPec: historico },
    loading,
  } = useHistoricoConsultaPecQuery({
    variables: {
      input: {
        idAtend,
        isOrigemPec: true,
      },
    },
  })

  if (loading) return <LoadingIndicator />

  if (!historico) return <HistoricoNoDetailAvailable />

  const tipoParticipacaoCidadao = tipoParticipacaoAtendimentoRecord[historico.tipoParticipacaoCidadao]

  return (
    <HistoricoGapPanel
      idadeOrigemAtendimento={idadeOrigemAtendimento}
      informacoesAdministrativas={mountInfosAdministrativasAtendimentosRecentes(historico, true)}
      isAtendObservacao={isAtendObservacao}
      tipoParticipacaoCidadao={tipoParticipacaoCidadao}
    >
      <HistoricoSection section='S'>
        <HistoricoSubjetivoPecPanel evolucaoSubjetivo={historico.evolucaoSubjetivo} />
      </HistoricoSection>
      <HistoricoSection section='O'>
        <HistoricoObjetivoPecPanel
          evolucaoObjetivo={historico.evolucaoObjetivo}
          medicoes={convertMedicoesToHistoricoMedicaoModel(historico.medicoes)}
          examesResultados={historico.examesResultados}
          marcadorConsumoAlimentar={historico.marcadorConsumoAlimentar}
          isAtendObservacao={isAtendObservacao}
          ivcf={historico.ivcf}
          dataNascimentoCidadao={idadeOrigemAtendimento.dataNascimentoCidadao}
          dataAtendimento={idadeOrigemAtendimento.dataAtendimento}
          arquivosAnexados={historico.arquivosAnexados}
        />
      </HistoricoSection>
      <HistoricoSection section='A'>
        <HistoricoAvaliacaoPecPanel
          evolucaoAvaliacao={historico.evolucaoAvaliacao}
          evolucaoAlergia={historico.alergiaEvolucao}
          evolucaoProblema={convertToEvolucaoProblema(historico.evolucaoProblema)}
        />
      </HistoricoSection>
      <HistoricoSection section='P'>
        <HistoricoPlanoPecPanel
          evolucaoPlano={historico.evolucaoPlano}
          quantidadeAtestados={historico.quantidadeAtestados}
          quantidadeOrientacoes={historico.quantidadeOrientacoes}
          encaminhamentos={historico.encaminhamentos}
          encaminhamentosEspecializados={
            historico.encaminhamentosEspecializados as EncaminhamentoExternoEspecializado[]
          }
          prescricaoMedicamentos={historico.receitaMedicamento as ReceitaMedicamento[]}
          examesSolicitados={historico.requisicoesExames as SolicitacaoExame[]}
          planosDeCuidado={historico.planosDeCuidado}
          isAtendObservacao={isAtendObservacao}
          cuidadosCompartilhados={historico.cuidadosCompartilhados}
          guiasEncaminhamento={historico.guiasEncaminhamento}
        />
      </HistoricoSection>
      <HistoricoAntecedenteSection
        antecedentes={historico.antecedentes}
        cirurgiasInternacoes={historico.cirurgiasInternacoes}
        dataNascimento={dataNascimento}
      />
      <HistoricoSection section='RA'>
        <HistoricoRegistrosAnterioresPanel
          medicoes={historico.medicoes}
          registrosVacinacao={historico.vacinacao?.registrosVacinacao as RegistroVacinacao[]}
          dataNascimentoCidadao={idadeOrigemAtendimento.dataNascimentoCidadao}
        />
      </HistoricoSection>
      <HistoricoSection section='D'>
        <HistoricoDesfechoPecPanel
          condutas={historico.condutas}
          procedimentosAdministrativos={historico.procedimentosAtendimento?.map((proced) => proced.procedimento)}
          notificacoesCasoSuspeito={historico.notificacoesCasoSuspeito}
          racionalidadeEmSaude={historico.racionalidadeSaude}
        />
      </HistoricoSection>
    </HistoricoGapPanel>
  )
}

export default memo(HistoricoConsultaPanel)
